/**
 * Chuẩn hóa dữ liệu khách hàng cho kiểm tra nghi trùng (P04 §4.2; DEC-063 B, DEC-072 A).
 * Hàm thuần: không lưu kết quả, không sửa dữ liệu gốc (DEC-072 A: không tạo cột tỉnh, không ghi lại địa chỉ).
 */

/** Ngưỡng giống tên chuẩn hóa (pg_trgm similarity) – DEC-063 B, DEC-072 A. */
export const DUPLICATE_NAME_THRESHOLD = 0.6;

/** Bỏ dấu tiếng Việt, chữ thường, đ → d, ký tự không phải chữ/số thành khoảng trắng. Khớp f_search_norm của DB. */
export function foldVi(s: string | null | undefined): string {
  return (s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** SĐT: chỉ chữ số, lấy 9 số cuối (bỏ 0/84 đầu). Ít hơn 9 số → null. Khớp cột customers.phone_norm. */
export function normPhone(s: string | null | undefined): string | null {
  const d = (s ?? '').replace(/\D/g, '');
  return d.length >= 9 ? d.slice(-9) : null;
}

export function normEmail(s: string | null | undefined): string | null {
  const t = (s ?? '').trim().toLowerCase();
  return t || null;
}

/**
 * Từ chỉ loại hình pháp nhân, bỏ khi so tên ("Cty TNHH ABC" ≈ "Công ty ABC").
 * Dùng chung cho JS và SQL (regexp_replace trên f_search_norm(name) – đã bỏ dấu).
 */
export const LEGAL_WORDS = [
  'cong ty', 'cty', 'ct', 'tnhh', 'mtv', 'co phan', 'cp', 'dntn', 'doanh nghiep tu nhan', 'doanh nghiep',
  'tap doan', 'tong cong ty', 'chi nhanh', 'van phong dai dien', 'xi nghiep', 'hop tac xa', 'htx',
  'trach nhiem huu han', 'mot thanh vien', 'thuong mai', 'tm', 'dich vu', 'dv', 'san xuat', 'sx',
  'xuat nhap khau', 'xnk', 'company', 'co', 'ltd', 'limited', 'jsc', 'corp', 'corporation', 'inc', 'group', 'vietnam', 'viet nam', 'vn',
] as const;
/** Mẫu regex (cú pháp PostgreSQL \m \M) bỏ từ pháp nhân – truyền làm tham số SQL. */
export const LEGAL_WORDS_PG_PATTERN = `\\m(${[...LEGAL_WORDS].sort((a, b) => b.length - a.length).join('|')})\\M`;
const LEGAL_JS = new RegExp(`\\b(${[...LEGAL_WORDS].sort((a, b) => b.length - a.length).join('|')})\\b`, 'g');

/** Tên dùng để so nghi trùng: bỏ dấu + bỏ từ pháp nhân. Rỗng sau khi bỏ thì dùng tên bỏ dấu. */
export function normCompanyName(s: string | null | undefined): string {
  const f = foldVi(s);
  const stripped = f.replace(LEGAL_JS, ' ').replace(/\s+/g, ' ').trim();
  return stripped || f;
}

// ---------------------------------------------------------------------------
// Tỉnh/thành – suy ra LÚC KIỂM TRA từ địa chỉ đã lưu (DEC-072 A). Không lưu, không sửa địa chỉ.
// Danh mục 34 tỉnh/thành hiện hành (Nghị quyết 202/2025/QH15, từ 01/07/2025) + tên cũ trước sáp nhập.
// Tên cũ quy về tỉnh mới để "cùng tỉnh" so đúng sau sáp nhập.

/** Mã tỉnh hiện hành → tên hiện hành + các tên/biệt danh cũ (có dấu, sẽ được chuẩn hóa). */
export const PROVINCES: Record<string, { name: string; aliases: string[] }> = {
  HN: { name: 'Hà Nội', aliases: ['Hà Nội', 'Ha Noi', 'Hanoi', 'TP Hà Nội'] },
  HUE: { name: 'Huế', aliases: ['Thành phố Huế', 'TP Huế', 'Thừa Thiên Huế', 'Thừa Thiên - Huế', 'TT Huế', 'Huế'] },
  LCH: { name: 'Lai Châu', aliases: ['Lai Châu'] },
  DBI: { name: 'Điện Biên', aliases: ['Điện Biên'] },
  SLA: { name: 'Sơn La', aliases: ['Sơn La'] },
  LSN: { name: 'Lạng Sơn', aliases: ['Lạng Sơn'] },
  QNI: { name: 'Quảng Ninh', aliases: ['Quảng Ninh', 'Hạ Long'] },
  THA: { name: 'Thanh Hóa', aliases: ['Thanh Hóa', 'Thanh Hoá'] },
  NAN: { name: 'Nghệ An', aliases: ['Nghệ An', 'Vinh'] },
  HTI: { name: 'Hà Tĩnh', aliases: ['Hà Tĩnh'] },
  CBA: { name: 'Cao Bằng', aliases: ['Cao Bằng'] },
  TQU: { name: 'Tuyên Quang', aliases: ['Tuyên Quang', 'Hà Giang'] },
  LCA: { name: 'Lào Cai', aliases: ['Lào Cai', 'Yên Bái'] },
  TNG: { name: 'Thái Nguyên', aliases: ['Thái Nguyên', 'Bắc Kạn', 'Bắc Cạn'] },
  PTH: { name: 'Phú Thọ', aliases: ['Phú Thọ', 'Vĩnh Phúc', 'Hòa Bình', 'Hoà Bình', 'Việt Trì', 'Vĩnh Yên'] },
  BNI: { name: 'Bắc Ninh', aliases: ['Bắc Ninh', 'Bắc Giang'] },
  HYE: { name: 'Hưng Yên', aliases: ['Hưng Yên', 'Thái Bình'] },
  HPG: { name: 'Hải Phòng', aliases: ['Hải Phòng', 'Hai Phong', 'Hải Dương'] },
  NBI: { name: 'Ninh Bình', aliases: ['Ninh Bình', 'Hà Nam', 'Nam Định'] },
  QTR: { name: 'Quảng Trị', aliases: ['Quảng Trị', 'Quảng Bình', 'Đồng Hới'] },
  DNG: { name: 'Đà Nẵng', aliases: ['Đà Nẵng', 'Da Nang', 'Danang', 'Quảng Nam', 'Tam Kỳ', 'Hội An'] },
  QNG: { name: 'Quảng Ngãi', aliases: ['Quảng Ngãi', 'Kon Tum', 'Kontum'] },
  GLA: { name: 'Gia Lai', aliases: ['Gia Lai', 'Bình Định', 'Pleiku', 'Quy Nhơn'] },
  KHA: { name: 'Khánh Hòa', aliases: ['Khánh Hòa', 'Khánh Hoà', 'Ninh Thuận', 'Nha Trang', 'Cam Ranh', 'Phan Rang'] },
  LDO: { name: 'Lâm Đồng', aliases: ['Lâm Đồng', 'Đắk Nông', 'Đăk Nông', 'Dak Nong', 'Bình Thuận', 'Đà Lạt', 'Phan Thiết'] },
  DLK: { name: 'Đắk Lắk', aliases: ['Đắk Lắk', 'Đăk Lăk', 'Dak Lak', 'Daklak', 'Phú Yên', 'Buôn Ma Thuột', 'Tuy Hòa'] },
  HCM: {
    name: 'TP. Hồ Chí Minh',
    aliases: ['Hồ Chí Minh', 'TP HCM', 'TPHCM', 'TP.HCM', 'HCM', 'HCMC', 'Sài Gòn', 'Saigon', 'Bình Dương', 'Thủ Dầu Một',
      'Bà Rịa - Vũng Tàu', 'Bà Rịa Vũng Tàu', 'BR-VT', 'BRVT', 'Vũng Tàu', 'Bà Rịa'],
  },
  DNA: { name: 'Đồng Nai', aliases: ['Đồng Nai', 'Bình Phước', 'Biên Hòa', 'Biên Hoà'] },
  TNI: { name: 'Tây Ninh', aliases: ['Tây Ninh', 'Long An', 'Tân An'] },
  CTO: { name: 'Cần Thơ', aliases: ['Cần Thơ', 'Sóc Trăng', 'Hậu Giang', 'Vị Thanh'] },
  VLO: { name: 'Vĩnh Long', aliases: ['Vĩnh Long', 'Bến Tre', 'Trà Vinh'] },
  DTH: { name: 'Đồng Tháp', aliases: ['Đồng Tháp', 'Tiền Giang', 'Mỹ Tho', 'Cao Lãnh'] },
  CMA: { name: 'Cà Mau', aliases: ['Cà Mau', 'Bạc Liêu'] },
  AGI: { name: 'An Giang', aliases: ['An Giang', 'Kiên Giang', 'Long Xuyên', 'Rạch Giá', 'Phú Quốc'] },
};

const ALIAS_INDEX: { alias: string; code: string }[] = Object.entries(PROVINCES)
  .flatMap(([code, p]) => p.aliases.map((a) => ({ alias: foldVi(a), code })))
  .filter((x) => x.alias)
  .sort((a, b) => b.alias.length - a.alias.length);

/**
 * Suy ra tỉnh/thành hiện hành từ địa chỉ tự do. Lấy tên tỉnh xuất hiện SAU CÙNG trong địa chỉ
 * (tỉnh thường đứng cuối; tránh nhầm tên đường kiểu "đường Quảng Nam"). Không chắc → null.
 */
export function deriveProvince(address: string | null | undefined): string | null {
  const a = ` ${foldVi(address)} `;
  if (a.trim() === '') return null;
  let best: { code: string; end: number; len: number } | null = null;
  for (const { alias, code } of ALIAS_INDEX) {
    const needle = ` ${alias} `;
    const i = a.lastIndexOf(needle);
    if (i < 0) continue;
    const end = i + needle.length;
    if (!best || end > best.end || (end === best.end && alias.length > best.len)) best = { code, end, len: alias.length };
  }
  return best?.code ?? null;
}
